import { useTranslation, Trans } from 'react-i18next';
import { SitePricingDiscount } from '../../../store/features/discounts/types';
import { DiscountStatuses } from '../../../types/enums';
import React from 'react';
import { isZeroDiscountAndVipSuccess } from '../../../../../utils/discountValidation';

interface UseGetAppliedDiscountMessageProps {
  discount?: SitePricingDiscount;
  code?: string;
}

export function useGetAppliedDiscountMessage(
  props: UseGetAppliedDiscountMessageProps,
) {
  const {
    discount: discountData,
    discount: {
      status,
      minNumberOfTickets,
      discount,
      productNames = [],
      claims,
      contactEmail,
      contactTelephone,
      delegateTypesNames,
    } = {},
    code,
  } = props;
  const { i18n, t } = useTranslation();

  if (isZeroDiscountAndVipSuccess(discountData)) {
    return {
      title: t(
        'site.pricing.discount.vip-code-discount.zero-value.accepted.message',
      ),
      text: undefined,
    };
  }

  function getEnding(quantity = 0) {
    if (quantity === 0) {
      return '';
    }

    switch (quantity % 10) {
      case 1:
        return quantity + 'st';
      case 2:
        return quantity + 'nd';
      case 3:
        return quantity + 'rd';
      default:
        return quantity + 'th';
    }
  }

  switch (status) {
    case DiscountStatuses.MSG_FIXED:
      return {
        title: t('site.pricing.discount.multi-buy.success.title'),
        text: t('site.pricing.discount.multi-buy.MSG_FIXED.success.message', {
          0: minNumberOfTickets,
          1: discount,
          2:
            i18n.language === 'en'
              ? getEnding(minNumberOfTickets)
              : minNumberOfTickets,
        }),
      };
    case DiscountStatuses.MSG_PERCENTAGE:
      return {
        title: t('site.pricing.discount.multi-buy.success.title'),
        text: t(
          'site.pricing.discount.multi-buy.MSG_PERCENTAGE.success.message',
          {
            0: minNumberOfTickets,
            1: discount,
            2:
              i18n.language === 'en'
                ? getEnding(minNumberOfTickets)
                : minNumberOfTickets,
          },
        ),
      };
    case DiscountStatuses.APPLIED:
      return {
        title: t(
          'site.pricing.discount.package-discount.APPLIED.success.title',
          { 0: discount },
        ),
      };
    case DiscountStatuses.VIP_SUCCESS_PERCENTAGE:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_SUCCESS_PERCENTAGE.success.message',
          { 0: discount },
        ),
      };
    case DiscountStatuses.VIP_SUCCESS_PERCENTAGE_FOR_PRODUCTS:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_SUCCESS_PERCENTAGE.success.message',
          { 0: discount, 1: productNames.join(', ') },
        ),
      };
    case DiscountStatuses.VIP_SUCCESS_FIXED:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_SUCCESS_FIXED.success.message',
          { 0: discount },
        ),
      };
    case DiscountStatuses.VIP_SUCCESS_FIXED_FOR_PRODUCTS:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_SUCCESS_FIXED_FOR_PRODUCTS.success.message',
          { 0: discount, 1: productNames.join(', ') },
        ),
      };
    case DiscountStatuses.VIP_NOT_ENOUGH_CLAIMS_PERCENTAGE:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text:
          claims === 1
            ? t(
                'site.pricing.discount.vip-code-discount.VIP_NOT_ENOUGH_CLAIMS_PERCENTAGE.single-claim.success.message',
                {
                  0: discount,
                  1: claims,
                },
              )
            : t(
                'site.pricing.discount.vip-code-discount.VIP_NOT_ENOUGH_CLAIMS_PERCENTAGE.multiple-claim.success.message',
                {
                  0: discount,
                  1: claims,
                },
              ),
      };
    case DiscountStatuses.VIP_NOT_ENOUGH_CLAIMS_PERCENTAGE_FOR_PRODUCTS:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_NOT_ENOUGH_CLAIMS_PERCENTAGE_FOR_PRODUCTS.success.message',
          { 0: discount, 1: productNames.join(', '), 2: claims },
        ),
      };
    case DiscountStatuses.VIP_NOT_ENOUGH_CLAIMS_FIXED:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text:
          claims === 1
            ? t(
                'site.pricing.discount.vip-code-discount.VIP_NOT_ENOUGH_CLAIMS_FIXED.single-claim.success.message',
                {
                  0: discount,
                  1: claims,
                },
              )
            : t(
                'site.pricing.discount.vip-code-discount.VIP_NOT_ENOUGH_CLAIMS_FIXED.multiple-claim.success.message',
                {
                  0: discount,
                  1: claims,
                },
              ),
      };
    case DiscountStatuses.VIP_NOT_ENOUGH_CLAIMS_FIXED_FOR_PRODUCTS:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_NOT_ENOUGH_CLAIMS_FIXED_FOR_PRODUCTS.success.message',
          { 0: discount, 1: productNames.join(', '), 2: claims },
        ),
      };
    case DiscountStatuses.NO_CODE_EXISTS:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.NO_CODE_EXISTS.message',
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.NO_CODE_EXISTS.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.CODE_EXPIRED:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.CODE_EXPIRED.message',
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.CODE_EXPIRED.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.MAX_CLAIMS_REACHED:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.MAX_CLAIMS_REACHED.message',
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.MAX_CLAIMS_REACHED.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.CODE_IS_AVAILABLE_ONLY_FOR_PRODUCTS:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.CODE_IS_AVAILABLE_ONLY_FOR_PRODUCTS.message',
          { 0: productNames.join(', ') },
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.CODE_IS_AVAILABLE_ONLY_FOR_PRODUCTS.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.CODE_IS_AVAILABLE_ONLY_FOR_DELEGATE_TYPES:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.CODE_IS_AVAILABLE_ONLY_FOR_DELEGATE_TYPES.message',
          { 0: delegateTypesNames?.join(', ') },
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.CODE_IS_AVAILABLE_ONLY_FOR_DELEGATE_TYPES.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.CODE_IS_AVAILABLE_ONLY_FOR_DELEGATE_TYPES_AND_FOR_PRODUCTS:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.CODE_IS_AVAILABLE_ONLY_FOR_DELEGATE_TYPES_AND_FOR_PRODUCTS.message',
          { 0: delegateTypesNames?.join(', '), 1: productNames?.join(', ') },
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.CODE_IS_AVAILABLE_ONLY_FOR_DELEGATE_TYPES_AND_FOR_PRODUCTS.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.CODE_IS_DISABLED_FOR_EARLY_BIRD:
      return {
        title: t(
          'site.pricing.discount.vip-code.validation.CODE_IS_DISABLED_FOR_EARLY_BIRD.message',
          { 0: code },
        ),
        text: (
          <Trans
            i18nKey="site.pricing.discount.vip-code.validation.CODE_IS_DISABLED_FOR_EARLY_BIRD.contact"
            values={{ 0: contactTelephone, 1: contactEmail }}
          >
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </Trans>
        ),
      };
    case DiscountStatuses.VIP_SUCCESS_FIXED_FOR_DELEGATE:
    case DiscountStatuses.VIP_SUCCESS_PERCENTAGE_FOR_DELEGATE:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text: t(
          'site.pricing.discount.vip-code-discount.VIP_SUCCESS_FIXED_FOR_DELEGATE-and-VIP_SUCCESS_PERCENTAGE_FOR_DELEGATE.success.message',
          { 0: discount, 1: delegateTypesNames?.join(', ') },
        ),
      };
    case DiscountStatuses.VIP_SUCCESS_PERCENTAGE_MULTI_BUY:
    case DiscountStatuses.VIP_SUCCESS_FIXED_MULTI_BUY:
      return {
        title: t('site.pricing.discount.vip-code-discount.success.title', {
          0: code,
        }),
        text:
          claims === 1
            ? t(
                'site.pricing.discount.vip-code-discount.VIP_SUCCESS_PERCENTAGE_MULTI_BUY-and-VIP_SUCCESS_FIXED_MULTI_BUY.single-claim.success.message',
                {
                  0: discount,
                  1: claims,
                },
              )
            : t(
                'site.pricing.discount.vip-code-discount.VIP_SUCCESS_PERCENTAGE_MULTI_BUY-and-VIP_SUCCESS_FIXED_MULTI_BUY.multiple-claim.success.message',
                {
                  0: discount,
                  1: claims,
                },
              ),
      };
    default:
      return {
        title: t(
          'site.pricing.discount.vip-code-discount.withoutStatus.success.title',
          { 0: discount },
        ),
        text: t(
          'site.pricing.discount.vip-code-discount.withoutStatus.success.message',
        ),
      };
  }
}
